/**
 * All Requests View (Admin Only)
 *
 * Displays all requests across the organization with filtering,
 * sorting, search, and pagination. Only accessible to ADMIN role.
 */

import { renderIcon } from '../utils/icons';
import { createRequestList, type ExpenditureRequest } from '../components/request-list';
import { createSidebarLayout } from '../components/sidebar-layout';
import { exportRequestsToCSV } from '../utils/csv-export';
import { showToast } from '../components/toast';

/**
 * Create the All Requests view
 */
export function createAllRequestsView(): HTMLElement {
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Admin badge and header
  const header = document.createElement('div');
  header.className = 'mb-6';
  header.innerHTML = `
    <div class="flex items-center gap-2 mb-2">
      <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary">
        ${renderIcon('shield', { size: 'xs' }).outerHTML}
        Admin View
      </span>
    </div>
    <p class="text-sm text-muted-foreground mt-1">View and manage all expenditure requests across the organization</p>
  `;
  content.appendChild(header);

  // Request list container
  const requestListContainer = document.createElement('div');
  requestListContainer.id = 'request-list-container';
  content.appendChild(requestListContainer);

  // Initialize request list after DOM is ready
  setTimeout(() => {
    let allRequests: ExpenditureRequest[] = [];

    const requestList = createRequestList({
      parent: requestListContainer,
      title: 'Organization Requests',
      subtitle: 'All requests from all users',
      endpoint: '/requests',
      showFilters: true,
      showSearch: true,
      showStatusColumn: true,
      showCreatorColumn: true,
      emptyMessage: 'No requests found in the organization.',
      enableBulkSelection: true,
      showBulkActions: true,
      onBulkExport: (ids: string[]) => {
        // Filter requests by selected IDs
        const selectedRequests = allRequests.filter(r => ids.includes(r.id));
        exportRequestsToCSV(selectedRequests);
        showToast({
          type: 'success',
          message: `Exported ${selectedRequests.length} request${selectedRequests.length > 1 ? 's' : ''} to CSV`,
        });
      },
      viewId: 'all-requests',
      showColumnPicker: true,
    });

    // Store all requests for export functionality
    const originalMethod = Object.getPrototypeOf(requestList).loadRequests;
    if (originalMethod) {
      Object.getPrototypeOf(requestList).loadRequests = async function(...args: any[]) {
        const result = await originalMethod.apply(this, args);
        allRequests = this.allRequests.length > 0 ? this.allRequests : this.requests;
        return result;
      };
    }
  }, 0);

  return createSidebarLayout(content, {
    currentPath: '/admin/requests',
    title: 'All Requests',
    subtitle: 'Organization-wide request management',
  });
}

/**
 * All Requests view function for router
 */
export function allRequestsView(): HTMLElement {
  return createAllRequestsView();
}
